import type { MessageOrigin } from '../types/conversation';
import {
    getMediaFileNameFromSource,
    getOutboundPreviewMediaType,
    type TOutboundPreviewMediaType,
} from './outboundMultimedia';
import {
    getWarboxMessageCreatedAt,
    getWarboxMessageId,
    getWarboxMessageMediaItems,
    getWarboxMessageOrigin,
    warboxMessageHasMedia,
    type TWarboxMessageMedia,
} from './warboxMessageMedia';

export type TGalleryMediaCategory =
    | 'images'
    | 'videos'
    | 'audios'
    | 'documents';

export type TConversationMediaItem = {
    id: string;
    messageId: string;
    source: string;
    name: string;
    contentType?: string;
    mediaType: TGalleryMediaCategory;
    createdAt: Date | string;
    origin?: MessageOrigin;
};

export type TGroupedConversationMedia = Record<
    TGalleryMediaCategory | 'all',
    TConversationMediaItem[]
>;

export const GALLERY_MEDIA_CATEGORIES: TGalleryMediaCategory[] = [
    'images',
    'videos',
    'audios',
    'documents',
];

const mapPreviewTypeToGalleryCategory = (
    previewType: TOutboundPreviewMediaType
): TGalleryMediaCategory => {
    switch (previewType) {
        case 'image':
            return 'images';
        case 'video':
            return 'videos';
        case 'audio':
            return 'audios';
        default:
            return 'documents';
    }
};

const toConversationMediaItem = ({
    messageId,
    media,
    mediaIndex,
    createdAt,
    origin,
}: {
    messageId: string;
    media: TWarboxMessageMedia;
    mediaIndex: number;
    createdAt: Date | string;
    origin: MessageOrigin;
}): TConversationMediaItem => {
    const name =
        media.filename ||
        getMediaFileNameFromSource(media.url) ||
        'file';

    return {
        id: `${messageId}-${mediaIndex}`,
        messageId,
        source: media.url,
        name,
        contentType: media.contentType,
        mediaType: mapPreviewTypeToGalleryCategory(
            getOutboundPreviewMediaType(name, media.contentType, media.url)
        ),
        createdAt,
        origin,
    };
};

export const buildConversationMediaGallery = (
    messages: unknown[] = []
): TGroupedConversationMedia => {
    const items = messages
        .filter(warboxMessageHasMedia)
        .flatMap((message) => {
            const messageId = getWarboxMessageId(message);
            const createdAt = getWarboxMessageCreatedAt(message);
            const origin = getWarboxMessageOrigin(message);

            return getWarboxMessageMediaItems(message).map((media, index) =>
                toConversationMediaItem({
                    messageId,
                    media,
                    mediaIndex: index,
                    createdAt,
                    origin,
                })
            );
        })
        .sort(
            (left, right) =>
                new Date(right.createdAt).getTime() -
                new Date(left.createdAt).getTime()
        );

    return {
        all: items,
        images: items.filter((item) => item.mediaType === 'images'),
        videos: items.filter((item) => item.mediaType === 'videos'),
        audios: items.filter((item) => item.mediaType === 'audios'),
        documents: items.filter((item) => item.mediaType === 'documents'),
    };
};

export const downloadMediaFile = (source: string, name: string) => {
    const link = document.createElement('a');
    link.href = source;
    link.download = name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export { warboxMessageHasMedia };
